import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AthleteRegister, AthleteSelectionRecord, Federation, FederationRecognitionStatus, Page, SelectionLevel } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'federations', label: 'Federations' },
  { key: 'athletes', label: 'Athlete register' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function FederationsPage() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('YOUTH_OFFICER') ?? false;
  const [tab, setTab] = useState<TabKey>('federations');

  const federationsQ = useApiQuery<Page<Federation>>('/api/v1/m30/federations?limit=100');
  const federations = useMemo(() => federationsQ.data?.items ?? [], [federationsQ.data]);
  const federationName = (id: string) => federations.find((f) => f.id === id)?.name ?? id;

  return (
    <div>
      <PageHeader
        title="Federation & Athlete Register"
        subtitle="Sport governing-body recognition, athlete affiliation admin, anti-doping education, and selection records — federations.ts + athletes.ts."
      />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Youth & Sport Officer role — federation recognition, athlete review, and selection records require YOUTH_OFFICER." />
        </div>
      ) : null}
      {tab === 'federations' ? <FederationsTab canManage={canManage} federationsQ={federationsQ} /> : null}
      {tab === 'athletes' ? <AthletesTab canManage={canManage} federations={federations} federationName={federationName} /> : null}
    </div>
  );
}

// ── Federations ────────────────────────────────────────────────────────

function FederationsTab({ canManage, federationsQ }: { canManage: boolean; federationsQ: ReturnType<typeof useApiQuery<Page<Federation>>> }) {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = federationsQ.data?.items ?? [];
  const selected = items.find((f) => f.id === selectedId) ?? null;

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex justify-end">
          {canManage ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register federation
            </button>
          ) : null}
        </div>
        {federationsQ.error ? <ErrorNote message={federationsQ.error} /> : null}
        <div className="card overflow-x-auto">
          {federationsQ.loading ? (
            <Spinner />
          ) : items.length === 0 ? (
            <EmptyState title="No federations registered yet" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Discipline</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === f.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(f.id)}>
                    <td className="table-cell font-medium">{f.name}</td>
                    <td className="table-cell text-xs text-slate-500">{f.sportDiscipline}</td>
                    <td className="table-cell">
                      <StatusBadge status={f.recognitionStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selected ? (
          <SectionCard title={selected.name}>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Discipline</dt>
                <dd className="font-medium text-slate-800">{selected.sportDiscipline}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Contact</dt>
                <dd className="text-right font-medium text-slate-800">
                  {selected.contactName ?? '—'}
                  {selected.contactEmail ? <div className="text-xs font-normal text-slate-500">{selected.contactEmail}</div> : null}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Recognized since</dt>
                <dd className="font-medium text-slate-800">{formatDate(selected.recognizedAt)}</dd>
              </div>
            </dl>
            {canManage ? (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <StatusChangeForm federation={selected} onDone={federationsQ.reload} />
              </div>
            ) : null}
          </SectionCard>
        ) : (
          <SectionCard>
            <EmptyState title="Select a federation" />
          </SectionCard>
        )}
      </div>

      {showCreate ? (
        <CreateFederationModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            federationsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function StatusChangeForm({ federation, onDone }: { federation: Federation; onDone: () => void }) {
  const [status, setStatus] = useState<FederationRecognitionStatus>(federation.recognitionStatus);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/federations/${federation.id}/status`, { recognitionStatus: status, reason: reason || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Recognition status">
        <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as FederationRecognitionStatus)}>
          {(['RECOGNIZED', 'PROBATIONARY', 'SUSPENDED'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Reason (optional)">
        <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || status === federation.recognitionStatus}>
        {busy ? 'Saving…' : 'Update status'}
      </button>
    </div>
  );
}

function CreateFederationModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [sportDiscipline, setSportDiscipline] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/federations', {
        name,
        sportDiscipline,
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register federation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register a federation" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Seychelles Volleyball Federation" />
        </Field>
        <Field label="Sport / discipline">
          <input className="field-input" value={sportDiscipline} onChange={(e) => setSportDiscipline(e.target.value)} placeholder="e.g. Volleyball" />
        </Field>
        <Field label="Contact name">
          <input className="field-input" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact email">
            <input className="field-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
          <Field label="Contact phone">
            <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2 || sportDiscipline.trim().length < 2}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Athletes ───────────────────────────────────────────────────────────

function AthletesTab({ canManage, federations, federationName }: { canManage: boolean; federations: Federation[]; federationName: (id: string) => string }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const athletesQ = useApiQuery<Page<AthleteRegister>>(
    canManage ? `/api/v1/m30/athletes?limit=50${status ? `&status=${status}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}` : null,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = athletesQ.data?.items ?? [];
  const selected = items.find((a) => a.id === selectedId) ?? null;

  if (!canManage) {
    return <ErrorNote message="Reviewing the athlete register requires the Youth & Sport Officer role." />;
  }

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex gap-2">
          <input className="field-input" placeholder="Search name or NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {(['PENDING', 'ACTIVE', 'REJECTED', 'SUSPENDED', 'WITHDRAWN'] as const).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {athletesQ.error ? <ErrorNote message={athletesQ.error} /> : null}
        <div className="card overflow-x-auto">
          {athletesQ.loading ? (
            <Spinner />
          ) : items.length === 0 ? (
            <EmptyState title="No athlete registrations found" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Federation</th>
                  <th className="table-head">Anti-doping</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === a.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(a.id)}>
                    <td className="table-cell font-medium">
                      {a.fullName}
                      <div className="text-[11px] font-normal text-slate-400">{a.athleteNin}</div>
                    </td>
                    <td className="table-cell text-xs text-slate-500">{federationName(a.federationId)}</td>
                    <td className="table-cell text-xs">
                      {a.antiDopingEducationExpiresAt ? (
                        new Date(a.antiDopingEducationExpiresAt).getTime() > Date.now() ? (
                          <span className="text-emerald-600">Current to {formatDate(a.antiDopingEducationExpiresAt)}</span>
                        ) : (
                          <span className="text-rose-600">Expired {formatDate(a.antiDopingEducationExpiresAt)}</span>
                        )
                      ) : (
                        <span className="text-slate-400">Not completed</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={a.affiliationStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selected ? (
          <AthleteDetail key={selected.id} athlete={selected} federationName={federationName} onChanged={athletesQ.reload} />
        ) : (
          <SectionCard>
            <EmptyState title="Select an athlete" hint="Approve, reject, suspend, log anti-doping education, or record a selection." />
          </SectionCard>
        )}
      </div>
    </div>
  );
}

function AthleteDetail({ athlete, federationName, onChanged }: { athlete: AthleteRegister; federationName: (id: string) => string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectionsQ = useApiQuery<{ items: AthleteSelectionRecord[] }>(`/api/v1/m30/athletes/${athlete.id}/selections`);

  const act = async (action: 'approve' | 'reject' | 'suspend', reason?: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/athletes/${athlete.id}/${action}`, reason ? { reason } : {});
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const completeAntiDoping = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/athletes/${athlete.id}/anti-doping/complete`, {});
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard title={athlete.fullName}>
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">NIN</dt>
            <dd className="font-medium text-slate-800">{athlete.athleteNin}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Federation</dt>
            <dd className="font-medium text-slate-800">{federationName(athlete.federationId)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Club</dt>
            <dd className="font-medium text-slate-800">{athlete.club ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Date of birth</dt>
            <dd className="font-medium text-slate-800">{formatDate(athlete.dob)}</dd>
          </div>
          {athlete.suspensionReason ? (
            <div>
              <dt className="text-slate-500">Reason on file</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{athlete.suspensionReason}</dd>
            </div>
          ) : null}
        </dl>

        <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
          {athlete.affiliationStatus === 'PENDING' ? (
            <>
              <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('approve')} disabled={busy}>
                Approve affiliation
              </button>
              <button
                className="btn-danger !px-2.5 !py-1 text-xs"
                onClick={() => {
                  const reason = prompt('Rejection reason:');
                  if (reason) act('reject', reason);
                }}
                disabled={busy}
              >
                Reject
              </button>
            </>
          ) : null}
          {athlete.affiliationStatus === 'ACTIVE' ? (
            <button
              className="btn-danger !px-2.5 !py-1 text-xs"
              onClick={() => {
                const reason = prompt('Suspension reason:');
                if (reason) act('suspend', reason);
              }}
              disabled={busy}
            >
              Suspend
            </button>
          ) : null}
          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={completeAntiDoping} disabled={busy}>
            Log anti-doping education complete
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Selection records">
        {selectionsQ.loading ? (
          <Spinner label="Loading…" />
        ) : selectionsQ.error ? (
          <ErrorNote message={selectionsQ.error} />
        ) : (selectionsQ.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">No selection records yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {selectionsQ.data!.items.map((s) => (
              <li key={s.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{s.competitionName}</span>
                  <StatusBadge status={s.level} />
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {formatDate(s.selectedAt)}
                  {s.result ? ` — ${s.result}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
        {athlete.affiliationStatus === 'ACTIVE' ? (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <NewSelectionForm athleteId={athlete.id} onDone={selectionsQ.reload} />
          </div>
        ) : (
          <p className="mt-2 text-[11px] text-slate-400">Only an ACTIVE athlete can carry a new selection record.</p>
        )}
      </SectionCard>
    </div>
  );
}

function NewSelectionForm({ athleteId, onDone }: { athleteId: string; onDone: () => void }) {
  const [competitionName, setCompetitionName] = useState('');
  const [level, setLevel] = useState<SelectionLevel>('NATIONAL');
  const [selectedAt, setSelectedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/athletes/${athleteId}/selections`, {
        competitionName,
        level,
        selectedAt: new Date(`${selectedAt}T00:00:00.000Z`).toISOString(),
        result: result || undefined,
      });
      setCompetitionName('');
      setResult('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record selection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Competition">
        <input className="field-input" value={competitionName} onChange={(e) => setCompetitionName(e.target.value)} placeholder="e.g. Indian Ocean Island Games" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Level">
          <select className="field-input" value={level} onChange={(e) => setLevel(e.target.value as SelectionLevel)}>
            {(['CLUB', 'REGIONAL', 'NATIONAL', 'INTERNATIONAL'] as const).map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date">
          <input type="date" className="field-input" value={selectedAt} onChange={(e) => setSelectedAt(e.target.value)} />
        </Field>
      </div>
      <Field label="Result (optional)">
        <input className="field-input" value={result} onChange={(e) => setResult(e.target.value)} placeholder="e.g. Bronze medal, 10.72s" />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || competitionName.trim().length < 2}>
        {busy ? 'Recording…' : 'Record selection'}
      </button>
    </div>
  );
}
